/**
 * Formulaire — la charpente d'un formulaire : des sections titrées, des
 * rangées libellé · contrôle · aide · erreur, et les actions en bas, collées
 * au bas de l'écran sur un téléphone.
 *
 *   <Formulaire onEnvoyer={enregistrer} actions={<>
 *       <Button variant="secondary" onClick={annuler}>Annuler</Button>
 *       <Button type="submit" disabled={enCours}>Enregistrer</Button>
 *     </>}>
 *     <SectionDeFormulaire titre="Identité" description="Ce que les fans voient.">
 *       <Champ etiquette="Nom" value={nom} onChange={…} />
 *       <Select etiquette="Langue" …>…</Select>
 *     </SectionDeFormulaire>
 *     <SectionDeFormulaire titre="Notifications" colonnes={1}>
 *       <Switch rangee label="Nouvelle vente" checked={…} onChange={…} />
 *       <RangeeDeFormulaire etiquette="Couleur" aide="Celle des cartes et des graphes.">
 *         <ChoixDeCouleur … />
 *       </RangeeDeFormulaire>
 *     </SectionDeFormulaire>
 *   </Formulaire>
 *
 * Il COMPOSE `Champ`, `Select`, `Switch`, `Checkbox` — qui portent déjà leur
 * étiquette, leur aide et leur erreur — sans les refaire. `RangeeDeFormulaire`
 * est pour tout le reste (une couleur, un curseur, des puces, un contrôle
 * de l'app) : la même étiquette, la même aide, la même erreur, au même endroit.
 * `cote` met le libellé et son aide à gauche, le contrôle à droite, quand la
 * rangée a la place (≥ 512 px : un écran de réglages) ; sinon tout s'empile.
 * Les deux colonnes d'une section et `cote` se règlent sur la largeur du
 * FORMULAIRE (requêtes de conteneur), pas sur celle de l'écran : un formulaire
 * dans un panneau étroit d'un grand écran reste sur une colonne.
 *
 * Pas de trait entre les sections : l'espace les sépare (la langue validée).
 * Pas d'état ici : la valeur, la validation et l'envoi restent chez l'écran ;
 * `onEnvoyer` reçoit l'envoi du `<form>` (Entrée dans un champ, le bouton
 * `type="submit"`), le rechargement de la page déjà empêché.
 *
 * Successeur des formulaires de réglages de `panels/creators` (`creatorTabs`),
 * de `WalletAddressEditor`, `RoleEditorModal`, `TransactionComposerModal`, et
 * des formulaires d'Helios : chacun réécrivait l'étiquette, l'aide, l'erreur
 * et le pied d'actions.
 */
import { useId, type FormEvent, type ReactNode } from "react";

import { SousLeChamp } from "../Champ";
import { cn } from "../utils";
import "../champs.css";
import "./saisie.css";

export interface FormulaireProps {
  children: ReactNode;
  /** L'envoi : Entrée dans un champ, ou un bouton `type="submit"`. La page ne se recharge pas. */
  onEnvoyer?: (e: FormEvent<HTMLFormElement>) => void;
  /** Les boutons du pied — `<ActionsDeFormulaire>` les pose (collés en bas sur un téléphone). */
  actions?: ReactNode;
  /** À gauche du pied : l'action qui détruit (« Supprimer »), loin de « Enregistrer ». */
  actionsAGauche?: ReactNode;
  /** Le pied collé en bas sur un téléphone (défaut). `false` dans une modale, qui a son pied. */
  actionsCollees?: boolean;
  id?: string;
  "aria-label"?: string;
  /** Pas de validation du navigateur (la nôtre est en `erreur`). Défaut vrai. */
  noValidate?: boolean;
  className?: string;
}

export function Formulaire({
  children,
  onEnvoyer,
  actions,
  actionsAGauche,
  actionsCollees = true,
  id,
  "aria-label": ariaLabel,
  noValidate = true,
  className,
}: FormulaireProps) {
  return (
    <form
      id={id}
      aria-label={ariaLabel}
      noValidate={noValidate}
      className={cn("flex min-w-0 flex-col gap-8", className)}
      onSubmit={(e) => {
        e.preventDefault();
        onEnvoyer?.(e);
      }}
    >
      {children}
      {(actions !== undefined || actionsAGauche !== undefined) && (
        <ActionsDeFormulaire aGauche={actionsAGauche} collant={actionsCollees}>
          {actions}
        </ActionsDeFormulaire>
      )}
    </form>
  );
}

export interface SectionDeFormulaireProps {
  children: ReactNode;
  /** Le titre, en Almarena. */
  titre?: ReactNode;
  /** Sous le titre, en gris : à quoi sert la section. */
  description?: ReactNode;
  /** Les champs sur une colonne (défaut) ou deux quand la section a la place (≥ 448 px) — une seule sur un téléphone. */
  colonnes?: 1 | 2;
  /** À droite du titre : un lien, un bouton discret. */
  actions?: ReactNode;
  className?: string;
}

export function SectionDeFormulaire({ children, titre, description, colonnes = 1, actions, className }: SectionDeFormulaireProps) {
  const id = useId();
  return (
    <fieldset aria-labelledby={titre !== undefined ? id : undefined} className={cn("m-0 flex min-w-0 flex-col gap-4 border-0 p-0", className)}>
      {(titre !== undefined || description !== undefined || actions !== undefined) && (
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1">
            {titre !== undefined && (
              <h3 id={id} className="si-titre-section m-0">
                {titre}
              </h3>
            )}
            {description !== undefined && <p className="m-0 text-[13px] leading-snug text-muted">{description}</p>}
          </div>
          {actions !== undefined && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </div>
      )}
      {/* La place du FORMULAIRE, pas celle de l'écran : un panneau de 360 px sur un grand écran reste sur une colonne. */}
      <div className="@container min-w-0">
        <div className={cn("grid min-w-0 gap-4", colonnes === 2 && "@md:grid-cols-2 @md:gap-x-5")}>{children}</div>
      </div>
    </fieldset>
  );
}

export interface RangeeDeFormulaireProps {
  children: ReactNode;
  /** Le libellé, en capitales (la recette de l'étiquette d'un champ). */
  etiquette?: ReactNode;
  /** Après le libellé, en minuscules grises : « facultatif ». */
  facultatif?: ReactNode;
  aide?: ReactNode;
  /** À la place de l'aide, en rouge. Le contrôle, lui, se met au rouge par sa propre prop `erreur`. */
  erreur?: ReactNode;
  /** L'id du contrôle que le libellé désigne (un `<label htmlFor>`). Sans, le libellé nomme le groupe. */
  pour?: string;
  /** Libellé et aide à gauche, contrôle à droite, quand la rangée a la place (≥ 512 px). */
  cote?: boolean;
  /** La rangée prend les deux colonnes d'une section à deux colonnes (le `className` va au même cadre). */
  pleineLargeur?: boolean;
  className?: string;
}

export function RangeeDeFormulaire({
  children,
  etiquette,
  facultatif,
  aide,
  erreur,
  pour,
  cote = false,
  pleineLargeur = false,
  className,
}: RangeeDeFormulaireProps) {
  const id = useId();
  const idAide = `${id}-aide`;
  const idLibelle = `${id}-libelle`;
  const libelle =
    etiquette !== undefined ? (
      pour !== undefined ? (
        <label id={idLibelle} htmlFor={pour} className="ch-etiquette">
          {etiquette}
          {facultatif !== undefined && <span className="si-facultatif ml-1.5">{facultatif}</span>}
        </label>
      ) : (
        <span id={idLibelle} className="ch-etiquette">
          {etiquette}
          {facultatif !== undefined && <span className="si-facultatif ml-1.5">{facultatif}</span>}
        </span>
      )
    ) : null;

  const sous = <SousLeChamp id={idAide} aide={aide} erreur={erreur} />;

  return (
    <div className={cn("@container min-w-0", pleineLargeur && "@md:col-span-2", className)}>
    <div
      role={pour === undefined && etiquette !== undefined ? "group" : undefined}
      aria-labelledby={pour === undefined && etiquette !== undefined ? idLibelle : undefined}
      aria-describedby={(erreur ?? aide) !== undefined ? idAide : undefined}
      className={cn(
        "flex min-w-0 flex-col gap-1.5",
        cote && "@lg:grid @lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] @lg:items-start @lg:gap-x-6",
      )}
    >
      {cote ? (
        <>
          <div className="flex min-w-0 flex-col gap-1 @lg:pt-2.5">
            {libelle}
            {sous}
          </div>
          <div className="min-w-0">{children}</div>
        </>
      ) : (
        <>
          {libelle}
          {children}
          {sous}
        </>
      )}
    </div>
    </div>
  );
}

export interface ActionsDeFormulaireProps {
  children?: ReactNode;
  /** À gauche : l'action qui détruit, loin de celle qui valide. */
  aGauche?: ReactNode;
  /** Collé en bas de l'écran sur un téléphone, les boutons à pleine largeur (défaut). */
  collant?: boolean;
  className?: string;
}

/** Le pied d'un formulaire : les boutons à droite ; sur un téléphone, collés en bas, sur un voile, à pleine largeur. */
export function ActionsDeFormulaire({ children, aGauche, collant = true, className }: ActionsDeFormulaireProps) {
  return (
    <div className={cn("si-pied-formulaire", className)} data-collant={collant || undefined}>
      {aGauche !== undefined && <div className="si-pied-formulaire-gauche">{aGauche}</div>}
      {children}
    </div>
  );
}
